import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Input, Table, Tabs, Modal, Tooltip } from "antd";
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  SearchOutlined,
  MailOutlined,
  PhoneOutlined,
  HistoryOutlined,
  SendOutlined,
  LinkedinFilled,
  CopyOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { fetchInviteTrackingInvites } from "../../redux/actions";
import { getInviteTrackingInviteHistoryApi } from "../../API/inviteTrackingApi";
import { GIGS_QUERY } from "../../components/PlaceBidModal/gigsQuery";
import history from "../../routes/history";
import "./ManageInvites.css";
import SendReminderModal from "./SendReminderModal";
import PlaceBidModal from "../QueryPage/components/organisms/PlaceBidModal";
import { isJobIncomplete, buildEditJobState } from "../QueryPage/components/organisms/SelectJobModal";
import bellEnabledIcon from "../../assets/icons/bell-enabled.svg";
import bellDisabledIcon from "../../assets/icons/bell-disabled.svg";
import FeatureGate from "../FeatureGate/FeatureGate";
import { canUseSubscriptionFeatures } from "../../shared/billing";

const { TabPane } = Tabs;

const STATUS_TABS = [
  { key: "all",             label: "All"             },
  { key: "invited",         label: "Invited"         },
  { key: "viewed",          label: "Viewed"          },
  { key: "onboarded",       label: "Onboarded"       },
];

const STATUS_QUERY = {
  invited: "invited",
  viewed: "viewed",
  "invite accepted": "invite accepted",
  onboarded: "onboarded",
};

const statusBadgeClass = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "invited")          return "mi-badge mi-badge--blue";
  if (v === "viewed")           return "mi-badge mi-badge--green";
  if (v === "invite accepted")  return "mi-badge mi-badge--teal";
  if (v === "onboarded")        return "mi-badge mi-badge--yellow";
  return "mi-badge mi-badge--gray";
};

const nextReminderClass = (l) => {
  const v = (l || "").toLowerCase();
  if (v.includes("ready"))         return "mi-badge mi-badge--blue";
  if (v.includes("not available")) return "mi-badge mi-badge--red";
  return "mi-badge mi-badge--gray";
};

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const getProfile = (item) =>
  item?.users?.Profiles ||
  item?.user?.Profiles ||
  item?.profile ||
  item?.Profile ||
  item?.talentProfile ||
  {};

const getNameParts = (item) => {
  const profile = getProfile(item);
  const fullName =
    item?.profileName ||
    item?.talentName ||
    item?.TalentName ||
    item?.fullName ||
    item?.name ||
    profile?.FullName ||
    "";
  const splitName = fullName.trim().split(/\s+/);
  const firstName = profile?.FirstName || item?.FirstName || splitName[0] || "";
  const lastName =
    profile?.LastName ||
    item?.LastName ||
    (splitName.length > 1 ? splitName.slice(1).join(" ") : "");
  return { firstName, lastName, fullName: `${firstName} ${lastName}`.trim() };
};

const getEmails = (item) =>
  asArray(
    item?.emails ||
      item?.Emails ||
      item?.invitedEmail ||
      item?.email ||
      item?.Email ||
      item?.EmailAddress ||
      item?.account?.emailAddress,
  );

const getPhones = (item) =>
  asArray(
    item?.phones ||
      item?.Phones ||
      item?.phone ||
      item?.Phone ||
      item?.MobilePhone ||
      item?.account?.mobilePhone,
  );

const extractLocationFromMessage = (message) => {
  if (!message || typeof message !== "string") return "";
  const match = message.match(/\bin\s+([A-Za-z][A-Za-z.\s]*,\s*[A-Za-z]{2,})\b/);
  return match ? match[1].trim() : "";
};

const getJobLocationFromItem = (item) => {
  const messages = [
    item?.message,
    item?.initialInvite?.message,
    item?.InvitedBy?.[0]?.message,
    ...(Array.isArray(item?.reminders) ? item.reminders.map((r) => r?.message) : []),
    ...(Array.isArray(item?.history) ? item.history.map((h) => h?.message) : []),
  ];
  for (const msg of messages) {
    const loc = extractLocationFromMessage(msg);
    if (loc) return loc;
  }
  return "";
};

const getInviteStatus = (item) =>
  item?.status ||
  item?.Status ||
  item?.inviteStatus ||
  item?.InvitedBy?.[0]?.Status ||
  "Invited";

const getProfilePictureUrl = (item) => {
  const profile = getProfile(item);
  return (
    item?.profilePictureUrl ||
    item?.profile_picture_url ||
    item?.pictureUrl ||
    item?.avatarUrl ||
    profile?.profilePictureUrl ||
    profile?.PictureUrl ||
    profile?.pictureUrl ||
    null
  );
};

const formatStatus = (status) =>
  (status || "")
    .toString()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const getLastReminderEntry = (item) => {
  const reminders = item?.reminders || item?.Reminders || item?.InvitedBy?.[0]?.reminders;
  return Array.isArray(reminders) && reminders.length
    ? reminders[reminders.length - 1]
    : null;
};

const getSentAt = (item) => {
  const lastReminder = getLastReminderEntry(item);
  return (
    item?.lastContactAt ||
    lastReminder?.sentAt ||
    lastReminder?.SentAt ||
    lastReminder?.date ||
    item?.sentAt ||
    item?.InvitedBy?.[0]?.InviteDate ||
    item?.lastSentAt ||
    item?.LastSentAt ||
    item?.inviteDate
  );
};

const canSendReminder = (item) => {
  const sentAt = getSentAt(item);
  if (!sentAt) return false;
  return moment().diff(moment(sentAt), "hours") >= 72;
};

export default function ManageInvites() {
  const dispatch = useDispatch();
  const routeLocation = useLocation();
  const [search, setSearch]         = useState("");
  const [activeTab, setActiveTab]   = useState("all");
  const [historyModal, setHistoryModal]       = useState({ open: false, item: null });
  const [reminderModal, setReminderModal]     = useState({ open: false, item: null });
  const [placeBidModal, setPlaceBidModal]     = useState({ open: false, item: null });
  const [placeBidJobDetail, setPlaceBidJobDetail] = useState(null);
  const [placeBidJobLoading, setPlaceBidJobLoading] = useState(false);
  const [bidPlacedIds, setBidPlacedIds]       = useState([]);
  const [reminderSentIds, setReminderSentIds] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce]     = useState(false);
  const hasFetchStartedRef = useRef(false);

  useEffect(() => {
    if (routeLocation.state?.reopenPlaceBid && routeLocation.state?.reopenBidContext) {
      const ctx = routeLocation.state.reopenBidContext;
      setPlaceBidModal({
        open: true,
        item: {
          profileId: ctx.skillListingId,
          SkillListingId: ctx.skillListingId,
          jobId: ctx.preselectedJob?.jobId,
          jobTitle: ctx.preselectedJob?.title,
          profileName: ctx.talent?.name,
          talentTitle: ctx.talent?.title,
          skillListings: ctx.skillListings || [],
        },
      });
    }
  }, [routeLocation.state]);

  const invitedCandidates = useSelector(
    (s) => s?.inviteTrackingInvitesReducer?.invites
  );
  const loading = useSelector(
    (s) => s?.inviteTrackingInvitesReducer?.loading
  );

  useEffect(() => {
    if (loading) hasFetchStartedRef.current = true;
    if (!loading && hasFetchStartedRef.current) setHasLoadedOnce(true);
  }, [loading]);

  const totalCounts = useSelector(
    (s) => s?.inviteTrackingInvitesReducer?.totalCounts
  );
  const authToken = useSelector((s) => s?.authReducer?.user?.authToken);
  const impersonationId = useSelector((s) => s?.authReducer?.impersonationId);
  const creditsBalance = useSelector((s) => s?.plansReducer?.creditsBalance);
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures(
    JSON.parse(localStorage.getItem("userProfile") || "null")
  );
  const isPlanExpired =
    subscriptionFeaturesEnabled && creditsBalance?.data?.status === "expired";
  const placeBidApolloClient = useMemo(() => {
    const httpLink = new HttpLink({
      uri: `${process.env.REACT_APP_API_URL}/query`,
    });
    const authLink = setContext((_, { headers }) => ({
      headers: {
        ...headers,
        authorization: authToken ? `${authToken}` : "",
        ...(impersonationId ? { impersonationId } : {}),
      },
    }));

    return new ApolloClient({
      cache: new InMemoryCache(),
      link: authLink.concat(httpLink),
    });
  }, [authToken, impersonationId]);
  const accountId = useMemo(() => {
    try {
      const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      return userProfile?.MasterAccountId || userProfile?.AccountId;
    } catch (error) {
      return null;
    }
  }, []);

  useEffect(() => {
    const jobId =
      placeBidModal.item?.jobId ||
      placeBidModal.item?.JobId ||
      placeBidModal.item?.InvitedBy?.[0]?.JobId;
    const jobTitle =
      placeBidModal.item?.jobTitle ||
      placeBidModal.item?.JobTitle ||
      placeBidModal.item?.InvitedBy?.[0]?.jobTitle;
    if (!placeBidModal.open || !jobId) {
      setPlaceBidJobDetail(null);
      setPlaceBidJobLoading(false);
      return;
    }
    let cancelled = false;
    setPlaceBidJobLoading(true);
    placeBidApolloClient
      .query({
        query: GIGS_QUERY,
        variables: {
          all: jobTitle || "",
          _page: 1,
          _perPage: 50,
          _sort: "MOST_RECENT",
          JobOrderStatusName: "Published",
          MygigsProfileId: accountId,
        },
        fetchPolicy: "no-cache",
      })
      .then((res) => {
        if (cancelled) return;
        const gigs = res?.data?.gigs?.data || [];
        const normalizedTitle = (jobTitle || "").trim().toLowerCase();
        const match =
          gigs.find((g) => String(g._id) === String(jobId)) ||
          gigs.find((g) => String(g.JobOrderId) === String(jobId)) ||
          (normalizedTitle
            ? gigs.find((g) => (g.Title || "").trim().toLowerCase() === normalizedTitle)
            : null) ||
          null;
        setPlaceBidJobDetail(match);
      })
      .catch(() => {
        if (!cancelled) setPlaceBidJobDetail(null);
      })
      .finally(() => {
        if (!cancelled) setPlaceBidJobLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [placeBidModal.open, placeBidModal.item, placeBidApolloClient, accountId]);


  useEffect(() => {
    if (!accountId) return;
    const timer = setTimeout(() => {
      dispatch(
        fetchInviteTrackingInvites({
          accountId,
          search: search.trim(),
          status: STATUS_QUERY[activeTab],
          page: 1,
          limit: 10,
        }),
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [accountId, activeTab, dispatch, search]);

  useEffect(() => {
    if (!accountId) return;
    const pollInterval = setInterval(() => {
      dispatch(
        fetchInviteTrackingInvites({
          accountId,
          search: search.trim(),
          status: STATUS_QUERY[activeTab],
          page: 1,
          limit: 10,
        }),
      );
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [accountId, activeTab, dispatch, search]);

  const baseList = useMemo(
    () =>
      Array.isArray(invitedCandidates) && invitedCandidates.length
        ? invitedCandidates
        : [],
    [invitedCandidates]
  );

  const filtered = useMemo(
    () =>
      baseList.filter((item) => {
        const { fullName } = getNameParts(item);
        const name  = fullName.toLowerCase();
        const title = (item?.Title || item?.title || item?.jobTitle || "").toLowerCase();
        const ok    =
          !search ||
          name.includes(search.toLowerCase()) ||
          title.includes(search.toLowerCase());

        const status = getInviteStatus(item);
        const tab    =
          activeTab === "all" ||
          status.toLowerCase() === activeTab.toLowerCase();

        return ok && tab;
      }),
    [baseList, search, activeTab]
  );

  const tabCounts = useMemo(() => {
    const readCount = (keys) => {
      if (typeof totalCounts === "number") {
        return keys.some((key) => ["all", "total", "totalCount"].includes(key))
          ? totalCounts
          : null;
      }
      if (!totalCounts || typeof totalCounts !== "object") return null;
      const foundKey = keys.find((key) => totalCounts[key] != null);
      return foundKey ? totalCounts[foundKey] : null;
    };
    const allCount = readCount(["all", "total", "totalCount"]);
    const c = { all: baseList.length };
    if (allCount != null) c.all = allCount;
    STATUS_TABS.slice(1).forEach(({ key }) => {
      const apiCount = readCount([
        key,
        key.replace(/\s+/g, ""),
        key.replace(/\s+/g, "_"),
      ]);
      c[key] = apiCount != null ? apiCount : baseList.filter((item) => {
        const s = getInviteStatus(item);
        return s.toLowerCase() === key.toLowerCase();
      }).length;
    });
    return c;
  }, [baseList, totalCounts]);

  const handleViewHistory = async (item) => {
    const profileId = item?.profileId || item?.SkillListingId || item?.skillListingId || item?._id || item?.id;
    const jobId = item?.jobId || item?.JobId || item?.InvitedBy?.[0]?.JobId;
    setHistoryModal({ open: true, item });

    const response = await getInviteTrackingInviteHistoryApi({
      accountId,
      profileId,
      jobId,
    });
    const responseData = response?.data;
    const detail = Array.isArray(responseData?.data)
      ? responseData.data[0]
      : responseData?.data || responseData;

    if (detail) {
      setHistoryModal({
        open: true,
        item: {
          ...item,
          ...detail,
          inviteHistoryResponse: responseData,
        },
      });
    }
  };

  const columns = [
    {
      title: "Talent Profile",
      key: "talent",
      width: 180,
      render: (_, item) => {
        const { firstName: fn, lastName: ln, fullName } = getNameParts(item);
        const name    = fullName || "—";
        const picture = getProfilePictureUrl(item);
        const initials= `${fn[0] || ""}${ln[0] || ""}`.toUpperCase() || "?";
        const bgColor = item?.avatarColor || "#9ba5b0";
        const id      = item?.profileId || item?.SkillListingId || item?.skillListingId || item?._id || item?.id;
        const linkedin= item?.profileLinkedinUrl || item?.linkedinUrl || item?._linkedinUrl;
        const isOnboarded = getInviteStatus(item).toLowerCase() === "onboarded";
        const onboardedSkillListingId = item?.skillListings?.[0]?.SkillListingId;
        return (
          <div className="mi-talent">
            {picture ? (
              <img src={picture} alt={name} className="mi-talent__avatar" />
            ) : (
              <div className="mi-talent__initials" style={{ background: bgColor }}>
                {initials}
              </div>
            )}
            <div className="mi-talent__info">
              <div className="mi-talent__name-row">
                <span
                  className="mi-talent__name"
                  onClick={() => {
                    if (isOnboarded && onboardedSkillListingId) {
                      history.push(`/profiledetail/${onboardedSkillListingId}`);
                    } else if (id) {
                      history.push(`/external-detail/${id}`);
                    }
                  }}
                >
                  {name}
                </span>
                {linkedin && (
                  <a
                    href={/^https?:\/\//.test(linkedin) ? linkedin : `https://${linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LinkedinFilled className="mi-talent__linkedin" />
                  </a>
                )}
              </div>
              <div className="mi-talent__title">{item?.talentTitle || item?.Title || item?.title || ""}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Contact",
      key: "contact",
      width: 210,
      render: (_, item) => {
        const emails = getEmails(item);
        const phones = getPhones(item);
        const copy   = item?.showCopy;
        return (
          <div className="mi-contact">
            {emails.slice(0, 1).map((e, i) => (
              <div key={i} className="mi-contact__row">
                <MailOutlined className="mi-contact__icon" />
                <span>{e}</span>
                {copy && (
                  <CopyOutlined
                    className="mi-contact__copy"
                    onClick={() => navigator.clipboard?.writeText(e)}
                  />
                )}
              </div>
            ))}
            {phones.slice(0, 1).map((p, i) => (
              <div key={i} className="mi-contact__row">
                <PhoneOutlined className="mi-contact__icon" />
                <span>{p}</span>
                {copy && (
                  <CopyOutlined
                    className="mi-contact__copy"
                    onClick={() => navigator.clipboard?.writeText(p)}
                  />
                )}
              </div>
            ))}
            {!emails.length && !phones.length && (
              <span style={{ color: "#b0b9c3" }}>—</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Job Details",
      key: "job",
      width: 120,
      render: (_, item) => {
        const jobTitle = item?.jobTitle || item?.JobTitle || item?.InvitedBy?.[0]?.jobTitle || "—";
        const jobId    = item?.JobOrderId || item?.jobId || item?.JobId || item?.InvitedBy?.[0]?.JobId;
        return jobId ? (
          <span
            className="mi-job-link"
            onClick={() => history.push(`/jobdetail/${jobId}`)}
          >
            {jobTitle}
          </span>
        ) : (
          <span style={{ color: "#6b7280", fontSize: 13 }}>{jobTitle}</span>
        );
      },
    },
    {
      title: "Reminder Sent",
      key: "reminderSent",
      width: 110,
      render: (_, item) => {
        const count = item?.reminderCount ?? item?.ReminderCount ?? item?.InvitedBy?.[0]?.reminderCount;
        const max   = item?.reminderMax ?? 3;
        return (
          <span className="mi-reminder-count">
            {count != null ? `${count}/${max}` : "—"}
          </span>
        );
      },
    },
    {
      title: "Next Reminder",
      key: "nextReminder",
      width: 145,
      render: (_, item) => {
        const status = getInviteStatus(item).toLowerCase();
        const id = item?.profileId || item?.SkillListingId || item?.skillListingId || item?._id || item?.id;
        const label =
          status === "invited" || status === "viewed"
            ? canSendReminder(item) && !reminderSentIds.includes(id)
              ? "Ready to Send"
              : "Not Available Yet"
            : status === "onboarded"
            ? "Not Available Yet"
            : item?.nextReminderStatus ||
              item?.NextReminderStatus ||
              item?.InvitedBy?.[0]?.nextReminderStatus ||
              null;
        if (!label) return <span style={{ color: "#b0b9c3" }}>—</span>;
        return <span className={nextReminderClass(label)}>{label}</span>;
      },
    },
    {
      title: "Last Sent",
      key: "lastSent",
      width: 120,
      render: (_, item) => {
        const date = getSentAt(item);
        if (!date) return <span style={{ color: "#b0b9c3" }}>—</span>;
        return (
          <span style={{ fontSize: 13, color: "#374151" }}>
            {moment(date).format("MMM D, YYYY")}
          </span>
        );
      },
    },
    {
      title: "Sent by",
      key: "sentBy",
      width: 145,
      render: (_, item) => {
        const email =
          item?.account?.emailAddress ||
          item?.InvitedBy?.[0]?.EmployerEmail ||
          item?.sentBy ||
          item?.SentBy ||
          "—";
        return <span style={{ fontSize: 13, color: "#374151" }}>{email}</span>;
      },
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, item) => {
        const status = getInviteStatus(item);
        return <span className={statusBadgeClass(status)}>{formatStatus(status)}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, item) => {
        const id = item?.profileId || item?.SkillListingId || item?.skillListingId || item?._id || item?.id;
        const status = getInviteStatus(item).toLowerCase();
        const isInvited = status === "invited";
        const showBell = status === "invited" || status === "viewed";
        const reminderReady = showBell && canSendReminder(item) && !reminderSentIds.includes(id);
        const sendDisabled =
          status === "viewed" ||
          status === "invite accepted" ||
          status === "onboarded";
        const canPlaceBid = status === "onboarded";
        const bidAlreadyPlaced = Boolean(item?.bidPlaced) || bidPlacedIds.includes(id);
        return (
          <div className="mi-actions">
            <Tooltip title="View Invite History">
              <button
                className="mi-action-btn"
                onClick={() => handleViewHistory(item)}
              >
                <HistoryOutlined />
              </button>
            </Tooltip>
            {showBell ? (
              <Tooltip
                title={
                  reminderReady
                    ? "Send Next Reminder"
                    : "Send Next Reminder after 72 hours"
                }
              >
                {reminderReady ? (
                  <button
                    className="mi-action-btn mi-action-btn--bell"
                    onClick={() => setReminderModal({ open: true, item })}
                  >
                    <img
                      src={bellEnabledIcon}
                      alt=""
                      className="mi-action-icon"
                    />
                  </button>
                ) : (
                  <span
                    className="mi-disabled-action-wrap"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <span
                      className="mi-action-btn mi-action-btn--bell mi-action-btn--bell-disabled"
                      role="button"
                      aria-disabled="true"
                    >
                      <img
                        src={bellDisabledIcon}
                        alt=""
                        className="mi-action-icon"
                      />
                    </span>
                  </span>
                )}
              </Tooltip>
            ) : (
              <Tooltip title={bidAlreadyPlaced ? "Bid Already Placed" : canPlaceBid ? "Place a Bid" : "Send Invite"}>
                <button
                  className={`mi-action-btn${(sendDisabled && !canPlaceBid) || bidAlreadyPlaced ? " mi-action-btn--muted" : ""}`}
                  disabled={(sendDisabled && !canPlaceBid) || bidAlreadyPlaced}
                  onClick={() => setPlaceBidModal({ open: true, item })}
                >
                  <SendOutlined />
                </button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  if (isPlanExpired) {
    return (
      <FeatureGate
        featureName="Manage Invites"
        iconType="candidate"
        description="Your free trial has ended. Choose a plan to continue managing your invited talents."
        bottomText="Manage Invites is a paid feature and was given to you for free during your trial period."
      />
    );
  }

  return (
    <div className="mi-page">
      <div className="mi-header">
        <h1 className="mi-title">Manage Invites</h1>
        <p className="mi-subtitle">Manage and track all the invited talents.</p>
      </div>

      <div className="mi-search-wrap">
        <Input
          className="mi-search"
          prefix={<SearchOutlined style={{ color: "#c0c8d2" }} />}
          placeholder="Search talent"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </div>

      <Tabs className="mi-tabs" activeKey={activeTab} onChange={setActiveTab}>
        {STATUS_TABS.map(({ key, label }) => (
          <TabPane key={key} tab={`${label} (${tabCounts[key] ?? 0})`}>
            <Table
              className="mi-table"
              columns={columns}
              dataSource={filtered}
              rowKey={(item, i) => item?.SkillListingId || item?._id || i}
              loading={loading && !hasLoadedOnce}
              pagination={false}
              locale={{
                emptyText: (
                  <div className="mi-empty">no talent found</div>
                ),
              }}
              scroll={{ x: 1100 }}
            />
          </TabPane>
        ))}
      </Tabs>

      {reminderModal.open && (
        <SendReminderModal
          item={reminderModal.item}
          hideTemplateSelect
          onClose={() => setReminderModal({ open: false, item: null })}
          onSent={() => {
            const item = reminderModal.item;
            const id = item?.profileId || item?.SkillListingId || item?.skillListingId || item?._id || item?.id;
            setReminderSentIds((prev) => [...prev, id]);
            if (accountId) {
              dispatch(
                fetchInviteTrackingInvites({
                  accountId,
                  search: search.trim(),
                  status: STATUS_QUERY[activeTab],
                  page: 1,
                  limit: 10,
                }),
              );
            }
          }}
        />
      )}

      {placeBidModal.open && (() => {
        const item = placeBidModal.item || {};
        const { fullName } = getNameParts(item);
        const talent = {
          name: fullName,
          title: item?.Title || item?.title || item?.jobTitle || "",
          avatar: getProfilePictureUrl(item),
        };

        const jobId = item?.jobId || item?.JobId || item?.InvitedBy?.[0]?.JobId;
        const jobTitle = item?.jobTitle || item?.JobTitle || item?.InvitedBy?.[0]?.jobTitle;
        const jobLocation =
          item?.jobLocation ||
          item?.JobLocation ||
          item?.Location?.LocationWithCountry ||
          item?.location ||
          item?.InvitedBy?.[0]?.jobLocation ||
          getJobLocationFromItem(item) ||
          "";
        const jobType =
          item?.jobType ||
          item?.JobType ||
          item?.JobOrderTypeName ||
          item?.InvitedBy?.[0]?.jobType ||
          "";

        const skillListingId =
          item?.SkillListingId ||
          item?.skillListingId ||
          item?.profileId;

        const preselectedJob = jobId
          ? {
              jobId,
              JobOrderId: placeBidJobDetail?.JobOrderId || jobId,
              title: jobTitle,
              jobTitle,
              location: jobLocation,
              type: jobType,
              JobOrderTypes: jobType ? { JobOrderTypeName: jobType } : undefined,
            }
          : null;

        const jobFetchedAndIncomplete =
          !placeBidJobLoading && placeBidJobDetail && isJobIncomplete(placeBidJobDetail);

        if (placeBidJobLoading) {
          return (
            <Modal open={placeBidModal.open} footer={null} onCancel={() => setPlaceBidModal({ open: false, item: null })} centered closable={false}>
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <LoadingOutlined style={{ fontSize: 28 }} spin />
              </div>
            </Modal>
          );
        }

        return (
          <PlaceBidModal
            open={placeBidModal.open}
            talent={talent}
            talentType="internal"
            skillListingOptions={item?.skillListings || []}
            skillListingId={skillListingId}
            preselectedJob={preselectedJob}
            incompleteJob={jobFetchedAndIncomplete}
            onCompleteJob={() => {
              const state = {
                ...buildEditJobState(placeBidJobDetail),
                returnTo: routeLocation.pathname + routeLocation.search,
                reopenBidContext: { talent, talentType: "internal", skillListingId, preselectedJob, skillListings: item?.skillListings || [] },
              };
              setPlaceBidModal({ open: false, item: null });
              history.push({ pathname: "/create-job", state });
            }}
            onClose={() => setPlaceBidModal({ open: false, item: null })}
            onBidSuccess={() =>
              setBidPlacedIds((prev) => [...prev, item?.profileId])
            }
          />
        );
      })()}

      <Modal
        open={historyModal.open}
        onCancel={() => setHistoryModal({ open: false, item: null })}
        footer={null}
        width={460}
        className="mi-history-modal"
        wrapClassName="mi-history-modal-wrap"
        title="Invite History"
        maskStyle={{ background: "#00000080" }}
      >
        {historyModal.item && (() => {
          const item = historyModal.item;
          const { firstName: fn, lastName: ln, fullName } = getNameParts(item);
          const name = fullName;
          const pic = getProfilePictureUrl(item);
          const initials = `${fn[0]||""}${ln[0]||""}`.toUpperCase();
          const bgColor  = item?.avatarColor || "#9ba5b0";
          const status   = getInviteStatus(item);
          const inviteHistory = (item?.history || []).filter(
            (entry) => entry?.sentAt || entry?.viewedAt || entry?.date,
          );
          const mediumColor = (m) => {
            const medium = (m || "").toLowerCase();
            if (medium === "email")    return { bg: "#dbeafe", color: "#1d4ed8" };
            if (medium === "sms")      return { bg: "#f3f4f6", color: "#374151" };
            if (medium === "whatsapp") return { bg: "#dcfce7", color: "#15803d" };
            return { bg: "#f3f4f6", color: "#374151" };
          };
          const isMediumFailed = (entry, m) => {
            const medium = (m || "").toLowerCase();
            const failedList = asArray(entry?.failedMediums || entry?.failedMedium || entry?.FailedMediums);
            if (failedList.some((f) => (f || "").toLowerCase() === medium)) return true;
            const status = entry?.mediumStatus?.[medium] || entry?.mediumStatuses?.[medium];
            if ((status || "").toLowerCase() === "failed") return true;
            const delivery = item?.deliveryStatus?.[medium];
            if (delivery) return delivery.delivered !== true || delivery.failed === true;
            const track = item?.tracking?.[medium];
            return !track?.read;
          };
          return (
            <div className="mi-hm">
              <div className="mi-hm__candidate">
                {pic ? (
                  <img src={pic} alt={name} className="mi-hm__avatar" />
                ) : (
                  <div className="mi-hm__initials" style={{ background: bgColor }}>
                    {initials}
                  </div>
                )}
                <div className="mi-hm__info">
                  <span className="mi-hm__name">{name}</span>
                  <span className="mi-hm__status" style={
                    status.toLowerCase() === "invited"
                      ? { background: "#dbeafe", color: "#1d4ed8" }
                      : { background: "#d1fae5", color: "#065f46" }
                  }>
                    {status}
                  </span>
                </div>
              </div>

              <div className="mi-hm__timeline">
                {inviteHistory.map((entry, i) => (
                  <div key={i} className="mi-hm__entry">
                    <div className="mi-hm__entry-dot" />
                    <div className="mi-hm__entry-body">
                      <div className="mi-hm__entry-label">{entry.title || entry.label}</div>
                      <div className="mi-hm__entry-meta">
                        <span className="mi-hm__entry-email">
                          {item?.invitedEmail || getEmails(item)?.[0] || item?.InvitedBy?.[0]?.EmployerEmail || "—"}
                        </span>
                        <div className="mi-hm__entry-mediums">
                          {asArray(entry.mediums || entry.medium).map((m) => {
                            const failed = isMediumFailed(entry, m);
                            const s = failed ? { bg: "#fee2e2", color: "#b91c1c" } : mediumColor(m);
                            const medium = (m || "").toLowerCase();
                            return (
                              <span
                                key={m}
                                className={`mi-hm__medium-chip${failed ? " mi-hm__medium-chip--failed" : ""}`}
                                style={{ background: s.bg, color: s.color }}
                                {...(failed && {
                                  "data-tooltip": medium === "email" ? "Email not available" : "Number not available",
                                })}
                              >
                                {formatStatus(m)}
                                {failed && <span className="mi-hm__medium-chip-x">✕</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div className="mi-hm__entry-date">
                        {moment(entry.sentAt || entry.viewedAt || entry.date).format("MMM D, YYYY · h:mm A")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
